/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */
import cx from 'classnames'
import { css, jsx } from '@emotion/react'

import type { WithEuiThemeProps } from '@elastic/eui'
import { EuiIcon, EuiFlexGroup, EuiFlexItem, withEuiTheme } from '@elastic/eui'

import SupportFooter from './SupportFooter'

import type { Interpolation, Theme } from '@emotion/react'
import type { FunctionComponent } from 'react'

interface Props extends WithEuiThemeProps {
  loading?: boolean
  children: JSX.Element | JSX.Element[]
  className?: string
  includeSupportFooter?: boolean
  scrollPage?: boolean
  css?: Interpolation<Theme>
}

const LandingPage: FunctionComponent<Props> = ({
  loading,
  children,
  className,
  includeSupportFooter = true,
  scrollPage = false,
  theme,
}) => {
  const { euiTheme } = theme

  const landingPageStyle = css({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  })

  const scrollLandingStyle = css({
    alignItems: 'stretch',
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'auto',
  })

  const staticLandingStyle = css({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    '.landing-page-wrapper': {
      height: '100%',
      width: '100%',
    },
  })

  const leftStyle = css({
    flexDirection: 'column',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    [`@media (min-width: ${euiTheme.breakpoint.m}px)`]: {
      minWidth: 512,
    },
  })

  const landingPageLogoWrapperStyle = css({
    display: 'flex',
    alignItems: 'center',
    padding: '1.5rem',
    position: 'absolute',
    right: '-3.8rem',
    top: '42%',
    height: 120,
    width: 120,
    borderRadius: 60,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
    background: euiTheme.colors.emptyShade,
    '.euiTheme-dark &': {
      background: euiTheme.colors.backgroundLightText,
    },
    '.euiIcon': {
      height: '100%',
      width: '100%',
    },
    '.loading-indicator': {
      position: 'absolute',
      top: 0,
      left: 0,
      height: 120,
      width: 120,
      borderRadius: 60,
      '&.loading': {
        border: `3px solid ${euiTheme.colors.emptyShade}`,
        borderTopColor: euiTheme.colors.vis.euiColorVis0,
        animation: 'euiLoadingSpinner 0.8s infinite linear',
      },
    },
    [`@media (max-width: ${euiTheme.breakpoint.m}px)`]: {
      display: 'flex',
      flexDirection: 'column',
      position: 'initial',
      marginTop: euiTheme.size.base,
      height: 70,
      width: 70,
      borderRadius: 35,
      padding: 0,
      '.loading-indicator': {
        position: 'initial',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '-4.4rem',
        height: 70,
        width: 70,
        borderRadius: 35,
      },
      '.euiIcon': {
        height: '50%',
        width: '50%',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginRight: '0.15rem',
      },
    },
  })

  const leftContentStyle = css({
    marginTop: 'auto',
    paddingTop: '2rem',
    [`@media (max-width: ${euiTheme.breakpoint.s}px)`]: {
      paddingTop: 0,
    },
  })

  const rightSectionStyle = css({
    backgroundColor: euiTheme.colors.primary,
  })

  return (
    <div css={[landingPageStyle, scrollPage ? scrollLandingStyle : staticLandingStyle]}>
      <EuiFlexGroup className='landing-page-wrapper' gutterSize='none'>
        <EuiFlexItem css={leftStyle}>
          <div css={landingPageLogoWrapperStyle}>
            <div className={cx(`loading-indicator`, { loading })} />
            <EuiIcon type='logoElastic' size='original' aria-label='Elastic' />
          </div>

          <div css={leftContentStyle} className={className}>
            {children}
          </div>

          {includeSupportFooter ? <SupportFooter /> : null}
        </EuiFlexItem>

        <EuiFlexItem className='right' css={rightSectionStyle} />
      </EuiFlexGroup>
    </div>
  )
}

export default withEuiTheme(LandingPage)
